import togeojson from '@mapbox/togeojson';
import JSZip from 'jszip';
import shpjs from 'shpjs';

import { convertMultiPolygonsToPolygons } from './geometry';

export { default as GeoPreviewDialog } from './GeoPreviewDialog';

export const POLYGON_TYPE_OPTIONS = [
    { value: 'N', label: 'Padrão' },
    { value: 'F', label: 'Variável' },
    { value: 'T', label: 'Arremate' },
    { value: 'X', label: 'Exclusão' }
];

const ZIP_SIGNATURES = [
    [0x50, 0x4b, 0x03, 0x04],
    [0x50, 0x4b, 0x05, 0x06],
    [0x50, 0x4b, 0x07, 0x08]
];

export class GeoFileError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'GeoFileError';
        this.code = code;
    }
}

const getExtension = fileName => {
    const parts = String(fileName || '').toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
};

const removeExtension = fileName => String(fileName || '').replace(/\.[^.]+$/, '');

const isZip = buffer => {
    const bytes = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 4));

    return ZIP_SIGNATURES.some(signature =>
        signature.every((value, index) => bytes[index] === value)
    );
};

const readBlob = (blob, method) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new GeoFileError(
        'Não foi possível ler o arquivo selecionado.',
        'FILE_READ_ERROR'
    ));
    reader.onload = () => resolve(reader.result);

    if (method === 'text') {
        reader.readAsText(blob);
    } else {
        reader.readAsArrayBuffer(blob);
    }
});

const toArrayBuffer = async file => {
    if (file instanceof ArrayBuffer) {
        return file;
    }

    if (ArrayBuffer.isView(file)) {
        return file.buffer.slice(file.byteOffset, file.byteOffset + file.byteLength);
    }

    if (file && typeof file.arrayBuffer === 'function') {
        return file.arrayBuffer();
    }

    if (typeof Blob !== 'undefined' && file instanceof Blob) {
        return readBlob(file, 'arrayBuffer');
    }

    throw new GeoFileError(
        'O conteúdo informado não é um File, Blob, ArrayBuffer ou Uint8Array.',
        'INVALID_INPUT'
    );
};

const toText = async file => {
    if (typeof file === 'string') {
        return file;
    }

    if (file && typeof file.text === 'function') {
        return file.text();
    }

    if (typeof Blob !== 'undefined' && file instanceof Blob) {
        return readBlob(file, 'text');
    }

    const buffer = await toArrayBuffer(file);

    if (typeof TextDecoder !== 'undefined') {
        return new TextDecoder('utf-8').decode(buffer);
    }

    if (typeof Blob !== 'undefined') {
        return readBlob(new Blob([buffer]), 'text');
    }

    throw new GeoFileError(
        'Este navegador não possui suporte para decodificar o arquivo KML.',
        'TEXT_DECODER_UNAVAILABLE'
    );
};

const normalizeGeoJSON = geojson => {
    if (!geojson) {
        return [];
    }

    if (Array.isArray(geojson)) {
        return geojson.reduce(
            (features, item) => features.concat(normalizeGeoJSON(item)),
            []
        );
    }

    if (geojson.type === 'FeatureCollection') {
        return normalizeGeoJSON(geojson.features);
    }

    if (geojson.type === 'Feature') {
        if (!geojson.geometry) {
            return [];
        }

        return convertMultiPolygonsToPolygons([{
            ...geojson,
            properties: geojson.properties || {}
        }]);
    }

    if (geojson.type && geojson.coordinates) {
        return convertMultiPolygonsToPolygons([{
            type: 'Feature',
            properties: {},
            geometry: geojson
        }]);
    }

    return [];
};

const parseKml = kml => {
    if (typeof DOMParser === 'undefined') {
        throw new GeoFileError(
            'A leitura de KML requer a API DOMParser do navegador.',
            'DOM_PARSER_UNAVAILABLE'
        );
    }

    const document = new DOMParser().parseFromString(kml, 'text/xml');
    const parserErrors = document.getElementsByTagName('parsererror');

    if (parserErrors.length > 0) {
        throw new GeoFileError('O conteúdo KML é inválido.', 'INVALID_KML');
    }

    return normalizeGeoJSON(togeojson.kml(document));
};

const getArchiveFiles = archive => Object.keys(archive.files)
    .map(path => archive.files[path])
    .filter(file => !file.dir && file.name.indexOf('__MACOSX/') === -1);

const validateShapefileParts = files => {
    const names = files.map(file => file.name.toLowerCase());
    const shapeFiles = names.filter(name => getExtension(name) === 'shp');

    const missingDbf = shapeFiles.filter(shapeName => {
        const dbfName = `${removeExtension(shapeName)}.dbf`;
        return names.indexOf(dbfName) === -1;
    });

    if (missingDbf.length > 0) {
        throw new GeoFileError(
            `DBF não encontrado para: ${missingDbf.join(', ')}.`,
            'MISSING_DBF'
        );
    }
};

const parseArchive = async buffer => {
    let archive;

    try {
        archive = await JSZip.loadAsync(buffer);
    } catch (error) {
        throw new GeoFileError(
            'O arquivo ZIP ou KMZ está inválido ou corrompido.',
            'INVALID_ARCHIVE'
        );
    }

    const files = getArchiveFiles(archive);
    const shapeFiles = files.filter(file => getExtension(file.name) === 'shp');
    const kmlFiles = files.filter(file => getExtension(file.name) === 'kml');
    const kmzFiles = files.filter(file => getExtension(file.name) === 'kmz');
    let features = [];

    if (shapeFiles.length > 0) {
        validateShapefileParts(files);

        try {
            const shapeGeoJSON = await shpjs(buffer);
            features = features.concat(normalizeGeoJSON(shapeGeoJSON));
        } catch (error) {
            if (error instanceof GeoFileError) {
                throw error;
            }

            throw new GeoFileError(
                'Não foi possível converter o Shapefile para GeoJSON.',
                'INVALID_SHAPEFILE'
            );
        }
    }

    for (let index = 0; index < kmlFiles.length; index++) {
        const kml = await kmlFiles[index].async('string');
        features = features.concat(parseKml(kml));
    }

    for (let index = 0; index < kmzFiles.length; index++) {
        const kmz = await kmzFiles[index].async('arraybuffer');
        features = features.concat(await parseArchive(kmz));
    }

    if (shapeFiles.length === 0 && kmlFiles.length === 0 && kmzFiles.length === 0) {
        throw new GeoFileError(
            'O arquivo compactado não contém KML, KMZ ou Shapefile.',
            'UNSUPPORTED_ARCHIVE'
        );
    }

    return features;
};

/**
 * Converte um upload KML, KMZ ou ZIP de Shapefile em Features GeoJSON.
 *
 * O retorno é sempre Promise<Feature[]>. Para preservar os atributos do
 * Shapefile, o ZIP deve conter pares .shp e .dbf com o mesmo nome.
 */
export const readGeoFile = async file => {
    if (!file) {
        throw new GeoFileError('Nenhum arquivo foi informado.', 'FILE_REQUIRED');
    }

    const extension = getExtension(file.name);

    if (extension === 'kml') {
        return parseKml(await toText(file));
    }

    const buffer = await toArrayBuffer(file);

    if (extension === 'zip' || extension === 'kmz' || isZip(buffer)) {
        return parseArchive(buffer);
    }

    if (extension === 'shp' || extension === 'dbf') {
        throw new GeoFileError(
            'Compacte os arquivos .shp e .dbf no mesmo ZIP antes de importar.',
            'INCOMPLETE_SHAPEFILE'
        );
    }

    throw new GeoFileError(
        'Formato não suportado. Envie um arquivo KML, KMZ ou ZIP de Shapefile.',
        'UNSUPPORTED_FILE'
    );
};

export default readGeoFile;
